import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Mail, Trash2, Save, Shield } from 'lucide-react';
import { Snackbar, Alert } from '@mui/material';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '../../components/ui/alert-dialog';

import UserService from '../../services/UserService/UserService';
import RolePermissionsService from '../../services/RolePermissionsService/RolePermissionsService';
import User from '../../types/User';
import { RolePermissionsMatrix } from '../../types/RolePermission';
import useUserActions from '../../hooks/useUserActions';
import UserRole, { getUserRoleLabel } from '../../types/UserRole';
import useAuthRoles from '../../hooks/useAuthRoles';
import CustomTabs from '../../components/CustomTabs/CustomTabs';

function UserPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [editedUser, setEditedUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'permissions'>(
    'profile'
  );
  const [showErrorSnackbar, setShowErrorSnackbar] = useState<boolean>(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] =
    useState<boolean>(false);
  const { deleteUser, updateUserRole, resendInvite } = useUserActions();
  const { userIsJapetoAdmin, userIsSuperAdmin } = useAuthRoles();
  const [resendingInvite, setResendingInvite] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<UserRole>('READ_ONLY');
  const [rolePermissions, setRolePermissions] =
    useState<RolePermissionsMatrix | null>(null);

  useEffect(() => {
    if (user?.role) {
      setSelectedRole(user.role);
    }
    if (user) {
      setEditedUser(user);
    }
  }, [user?.role, user]);

  const onSuccessSnackbarClose = (): void => setShowSuccessSnackbar(false);
  const onErrorSnackbarClose = (): void => setShowErrorSnackbar(false);

  const openErrorSnackbar = (message: string): void => {
    setSnackbarMessage(message);
    setShowSuccessSnackbar(false);
    setShowErrorSnackbar(true);
  };

  const openSuccessSnackbar = (message: string): void => {
    setSnackbarMessage(message);
    setShowErrorSnackbar(false);
    setShowSuccessSnackbar(true);
  };

  const handleDeleteUser = async (userId: number): Promise<void> => {
    try {
      await deleteUser(userId);
      navigate('/users');
    } catch {
      openErrorSnackbar(
        'We were unable to delete the user. Please try again later.'
      );
    }
  };

  const handleUpdateUserRole = async (): Promise<void> => {
    if (!user) return;
    try {
      await updateUserRole(user.id, selectedRole);
      openSuccessSnackbar('User role updated successfully');
    } catch (error: any) {
      const message = error?.response?.data?.message;
      openErrorSnackbar(
        message ||
          'We were unable to update the user role. Please try again later.'
      );
    }
  };

  const handleResendInvite = async (userEmail: string): Promise<void> => {
    setResendingInvite(true);
    try {
      await resendInvite(userEmail);
      openSuccessSnackbar('Invite email sent successfully');
    } catch {
      openErrorSnackbar(
        'We were unable to resend the invite email. Please try again later.'
      );
    } finally {
      setResendingInvite(false);
    }
  };

  const handleSaveChanges = async () => {
    if (editedUser && user) {
      setUser(editedUser);
      try {
        await UserService.updateAnyUserDetails(
          editedUser.id,
          editedUser.email,
          editedUser.firstName,
          editedUser.lastName,
          editedUser.organisationId
        );
        setUser(editedUser);

        const emailChanged = editedUser.email !== user.email;
        if (emailChanged) {
          const message =
            user.status === 'INVITATION_PENDING'
              ? 'User details updated successfully. An invitation email has been resent to the new email address.'
              : 'User details updated successfully. A confirmation email has been sent to the new email address.';
          openSuccessSnackbar(message);
        } else {
          openSuccessSnackbar('User details updated successfully');
        }
      } catch (error: any) {
        openErrorSnackbar(
          error.message ||
            'We were unable to update the user details. Please try again later.'
        );
      }
    }
  };

  const getUser = useCallback(
    (signal?: AbortSignal): void => {
      const optionalArgs = signal ? [signal] : [];
      setIsLoadingUser(true);
      UserService.getUser(Number(id), ...optionalArgs)
        .then((data) => {
          if (data) {
            setUser(data);
          }
        })
        .catch((err) => {
          if (err.name === 'CanceledError') return;
          openErrorSnackbar(err.message);
        })
        .finally(() => setIsLoadingUser(false));
    },
    [id]
  );

  useEffect(() => {
    const controller = new AbortController();
    getUser(controller.signal);
    return () => controller.abort();
  }, [getUser]);

  useEffect(() => {
    const controller = new AbortController();
    RolePermissionsService.getRolePermissions(controller.signal)
      .then(setRolePermissions)
      .catch((err) => {
        if (err.name === 'CanceledError') return;
        openErrorSnackbar('We were unable to load the role permissions.');
      });
    return () => controller.abort();
  }, []);

  if (isLoadingUser) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return (
      <div className='space-y-6'>
        <div className='flex items-center space-x-4'>
          <Link to='/users'>
            <Button variant='ghost' size='sm'>
              Back to users
            </Button>
          </Link>
        </div>
        <div className='text-center py-8'>
          <p className='text-lg text-gray-500'>User not found</p>
        </div>
      </div>
    );
  }

  const selectedRolePermissions = rolePermissions?.[selectedRole] ?? [];

  const assignableRoles: UserRole[] = [
    'READ_ONLY',
    'READ_WRITE',
    'ORGANISATION_ADMIN'
  ];
  if (userIsJapetoAdmin()) assignableRoles.push('JAPETO_ADMIN');
  if (userIsSuperAdmin()) assignableRoles.push('SUPER_ADMIN');
  if (!assignableRoles.includes(user.role)) assignableRoles.push(user.role);

  return (
    <>
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={onSuccessSnackbarClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
      >
        <Alert
          onClose={onSuccessSnackbarClose}
          severity='success'
          variant='filled'
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={3000}
        onClose={onErrorSnackbarClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
      >
        <Alert onClose={onErrorSnackbarClose} severity='error' variant='filled'>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <div className='space-y-6 max-w-4xl'>
        <div className='flex items-center justify-between'>
          <Link to='/users'>
            <Button variant='ghost' size='sm'>
              Back to users
            </Button>
          </Link>
          <div className='flex space-x-2'>
            {user.status === 'INVITATION_PENDING' && (
              <Button
                variant='outline'
                onClick={() => handleResendInvite(user.email)}
                loading={resendingInvite}
                loadingText='Sending invite...'
              >
                <Mail className='mr-2 h-4 w-4' />
                Resend invite
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant='destructive'>
                  <Trash2 className='mr-2 h-4 w-4' />
                  Delete user
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete
                    the user account and remove all associated data.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => handleDeleteUser(user.id)}>
                    Delete user
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        <CustomTabs
          constrainWidth={false}
          hasContentBox
          tabs={[
            {
              label: 'Profile',
              value: 'profile',
              content: (
                <div>
                  <div className='space-y-4'>
                    <div>
                      <h3 className='font-semibold text-lg'>
                        User Information
                      </h3>
                      <p className='text-sm text-gray-600'>
                        Manage user profile and contact details
                      </p>
                    </div>
                    <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
                      <div className='space-y-2'>
                        <Label htmlFor='firstName'>First name</Label>
                        <Input
                          id='firstName'
                          value={editedUser?.firstName || ''}
                          onChange={(e) =>
                            setEditedUser((prev) =>
                              prev
                                ? { ...prev, firstName: e.target.value }
                                : null
                            )
                          }
                        />
                      </div>
                      <div className='space-y-2'>
                        <Label htmlFor='lastName'>Last name</Label>
                        <Input
                          id='lastName'
                          value={editedUser?.lastName || ''}
                          onChange={(e) =>
                            setEditedUser((prev) =>
                              prev
                                ? { ...prev, lastName: e.target.value }
                                : null
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className='flex items-end gap-4'>
                      <div className='space-y-2 flex-1'>
                        <Label htmlFor='email'>Email address</Label>
                        <Input
                          id='email'
                          type='email'
                          value={editedUser?.email || ''}
                          onChange={(e) =>
                            setEditedUser((prev) =>
                              prev ? { ...prev, email: e.target.value } : null
                            )
                          }
                        />
                      </div>
                      <Button onClick={handleSaveChanges}>
                        <Save className='mr-2 h-4 w-4' />
                        Save changes
                      </Button>
                    </div>
                  </div>
                </div>
              )
            },
            {
              label: 'Permissions',
              value: 'permissions',
              content: (
                <div className='space-y-6'>
                  <div>
                    <h3 className='font-semibold text-lg'>Access Control</h3>
                    <p className='text-sm text-gray-600'>
                      Manage user permissions and role assignments
                    </p>
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='role'>User Role</Label>
                    <Select
                      value={selectedRole}
                      onValueChange={(value) =>
                        setSelectedRole(value as UserRole)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Select a role' />
                      </SelectTrigger>
                      <SelectContent>
                        {assignableRoles.map((role) => (
                          <SelectItem key={role} value={role}>
                            {getUserRoleLabel(role)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className='space-y-4'>
                    <h4 className='font-medium text-lg flex items-center'>
                      <Shield className='mr-2 h-4 w-4' />
                      Role Permissions
                    </h4>
                    <div className='space-y-3 pl-6'>
                      {selectedRolePermissions.map((permission) => (
                        <div
                          key={permission.capability}
                          className='flex justify-between items-center'
                        >
                          <span className='text-sm'>{permission.label}</span>
                          <Badge
                            variant='outline'
                            className={
                              permission.allowed
                                ? 'text-green-600 border-green-600'
                                : 'text-red-600 border-red-600'
                            }
                          >
                            {permission.allowed ? 'Allowed' : 'Denied'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className='pt-4'>
                    <Button onClick={handleUpdateUserRole}>
                      <Save className='mr-2 h-4 w-4' />
                      Update permissions
                    </Button>
                  </div>
                </div>
              )
            }
          ]}
          value={activeTab}
          onValueChange={(val) =>
            setActiveTab(val as 'profile' | 'permissions')
          }
        />
      </div>
    </>
  );
}

export default UserPage;
